import type {
  WorkflowContext,
  WorkflowGroupOptions,
  WorkflowGroupStatusResponse,
  AssetWorkflowOptions,
  AssetState,
  Asset,
} from './types';
import { Workflow } from './Workflow';
import { AssetWorkflow } from './AssetWorkflow';
import { EditAssetWorkflow } from './EditAssetWorkflow';

export class WorkflowGroup {
  public readonly name: string;
  public readonly workflowGroupIcon: string;
  public readonly workflowGroupIndex: number;
  public readonly workflowGroupKey: string;
  public readonly workflowGroupDescription: string;
  public readonly isShipiumUser: boolean;
  public options: WorkflowGroupOptions;
  public workflowMap: Record<string, Workflow>;
  public workflows: Workflow[];

  constructor(options: WorkflowGroupOptions, isShipiumUser?: boolean) {
    this.name = options.workflowGroupName;
    this.isShipiumUser = isShipiumUser === true ? true : false;
    this.workflowGroupIcon = options.workflowGroupIcon;
    this.workflowGroupIndex = options.workflowGroupIndex;
    this.workflowGroupKey = options.workflowGroupKey;
    this.workflowGroupDescription = options.workflowGroupDescription;
    this.options = options;
    this.workflows = [];
    this.workflowMap = {};
  }

  public static constructWorkflowMap(workflows: Workflow[]): Map<string, Workflow> {
    return workflows.reduce((prev, curr) => {
      return prev.set(curr.workflowKey, curr);
    }, new Map<string, Workflow>());
  }

  public buildWorkflows(assetState: AssetState) {
    const workflowMap = this.options.workflowGroupWorkflowOptions
      .filter((opts) => !opts.shipiumOnly || this.isShipiumUser)
      .sort((a, b) => (a.workflowIndex < b.workflowIndex ? -1 : a.workflowIndex > b.workflowIndex ? 1 : 0))
      .reduce<Record<string, Workflow>>((prev, options) => {
        if (options.workflowType === 'editAssetWorkflow') {
          const editOptions = options as AssetWorkflowOptions;
          const assetType = editOptions.assetType;
          const assets = assetState[assetType];
          for (const asset of assets) {
            const editWorkflow = new EditAssetWorkflow(
              this.workflowGroupKey,
              editOptions,
              asset,
              assetState,
              this.isShipiumUser,
            );
            prev[editWorkflow.workflowKey] = editWorkflow;
          }
        } else if (options.workflowType === 'addAssetWorkflow') {
          const addOptions = options as AssetWorkflowOptions;
          const workflow = new AssetWorkflow(this.workflowGroupKey, addOptions, assetState, this.isShipiumUser);
          prev[workflow.workflowKey] = workflow;
        } else {
          const workflow = new Workflow(this.workflowGroupKey, options, assetState);
          prev[workflow.workflowKey] = workflow;
        }
        return prev;
      }, {});
    this.workflowMap = workflowMap;
    this.workflows = Object.values(workflowMap);
    return this.workflows;
  }

  public assets() {
    return this.workflows.reduce<Record<string, Asset>>((prev, curr) => {
      if (curr.workflowType === 'editAssetWorkflow') {
        const asset = (curr as EditAssetWorkflow).asset;
        prev[asset.urn!] = asset;
      }
      return prev;
    }, {});
  }

  public status(workflowContext: WorkflowContext): WorkflowGroupStatusResponse {
    return this.workflows
      .filter((wf) => wf.workflowType === 'configurationWorkflow' || wf.workflowType === 'addAssetWorkflow')
      .reduce<WorkflowGroupStatusResponse>(
        (prev, curr) => {
          const status = curr.status({ ...workflowContext, workflowKey: curr.workflowKey });
          if (status.workflowStatus === 'completed') {
            prev.completed.push(status);
            if (prev.completed.length + prev.disabled.length === this.workflows.length) {
              prev.workflowGroupStatus = 'completed';
            }
          } else if (status.workflowStatus === 'disabled') {
            prev.disabled.push(status);
          } else if (status.workflowStatus === 'inProgress') {
            prev.inProgress.push(status);
            prev.workflowGroupStatus = 'inProgress';
          } else if (status.workflowStatus === 'notStarted') {
            prev.notStarted.push(status);
            prev.workflowGroupStatus = 'notStarted';
          }
          return prev;
        },
        {
          workflowGroupKey: this.workflowGroupKey,
          workflowGroupStatus: 'disabled',
          notStarted: [],
          inProgress: [],
          completed: [],
          disabled: [],
        },
      );
  }
}
